import React, { useEffect, useState, useCallback } from 'react';
import { Smartphone, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { logger } from '../lib/logger';

/* InstallToHomeScreenCard
 *
 * Inline dashboard banner that appears after signup encouraging the user to
 * pin Apex Mind to their phone's home screen. Hidden if:
 *   • Already running in standalone (display-mode: standalone) — they did it
 *   • Dismissed via the X
 *   • Visitor is on desktop AND the dashboard is rarely accessed from desktop
 *     (we still show it — coaches/parents might want the desktop install too)
 */

const DISMISS_KEY = 'apex.installCard.dismissed.v1';

const inStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
};

const safeRead = (key) => {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch (e) {
    logger.debug('InstallToHomeScreenCard read blocked', e);
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
  } catch (e) {
    logger.debug('InstallToHomeScreenCard write blocked', e);
  }
};

export default function InstallToHomeScreenCard() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (inStandalone()) return; // already installed
    if (safeRead(DISMISS_KEY) === '1') return;
    setVisible(true);
  }, []);

  const dismiss = useCallback(() => {
    safeWrite(DISMISS_KEY, '1');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="relative rounded-2xl border border-[#004C54]/20 bg-gradient-to-br from-[#004C54]/8 via-white to-white p-5 sm:p-6 shadow-sm"
      data-testid="dashboard-install-card"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install reminder"
        data-testid="dashboard-install-card-dismiss"
        className="absolute top-3 right-3 h-7 w-7 inline-flex items-center justify-center rounded-full text-foreground/40 hover:text-foreground hover:bg-secondary transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="flex items-start gap-3 sm:gap-4">
        <div className="hidden sm:flex shrink-0 h-11 w-11 rounded-xl bg-[#004C54] text-white items-center justify-center">
          <Smartphone className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#004C54] font-bold mb-1.5">
            10-second setup
          </div>
          <h3 className="font-heading text-[16px] sm:text-[17px] font-bold text-foreground leading-snug">
            Pin Coach Lewis to your home screen.
          </h3>
          <p className="text-[12.5px] sm:text-[13px] text-foreground/65 mt-1.5 leading-snug max-w-[520px]">
            One tap from your lock screen, no app store. You&rsquo;ll get pre-game reminders and journal nudges (only if you opt in &mdash; we don&rsquo;t spam).
          </p>

          {/* Tiny inline benefit list to make value obvious without a click */}
          <ul className="mt-3 space-y-1.5">
            {[
              'Opens full-screen like a real app',
              'Works offline once it\u2019s on your home screen',
              'No App Store account required',
            ].map((line) => (
              <li key={line} className="flex items-start gap-2 text-[12px] text-foreground/70">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#004C54] mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <a
              href="/install"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#004C54] hover:bg-[#003a40] text-white text-[12.5px] font-semibold px-4 h-9 transition-colors"
              data-testid="dashboard-install-card-cta"
            >
              Show me how
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="text-[12px] text-foreground/45 hover:text-foreground/70"
              data-testid="dashboard-install-card-skip"
            >
              I&rsquo;ve already installed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
